import { useCallback, useEffect, useRef, useState } from 'react';

type UseAnimatedDropdownOptions = {
  closeOnOutsideClick?: boolean;
  initialCollapsed?: boolean;
};

type UseAnimatedDropdownReturn = {
  close: () => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  isClosing: boolean;
  isCollapsed: boolean;
  open: () => void;
  toggle: () => void;
};

export const useAnimatedDropdown = ({
  closeOnOutsideClick = false,
  initialCollapsed = true,
}: UseAnimatedDropdownOptions = {}): UseAnimatedDropdownReturn => {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
  const [isClosing, setIsClosing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback((): void => {
    setIsClosing(true);
  }, []);

  const open = useCallback((): void => {
    setIsCollapsed(false);
  }, []);

  const toggle = useCallback((): void => {
    setIsCollapsed(prev => !prev);
  }, []);

  useEffect(() => {
    if (!closeOnOutsideClick) {
      return;
    }

    const handleClickOutside = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node) && !isCollapsed) {
        close();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCollapsed, close, containerRef, closeOnOutsideClick]);

  useEffect(() => {
    const container = containerRef.current;
    if (isClosing && container) {
      const handleAnimationEnd = (): void => {
        setIsCollapsed(true);
        setIsClosing(false);
      };
      container.addEventListener('animationend', handleAnimationEnd);
      return (): void => {
        container.removeEventListener('animationend', handleAnimationEnd);
      };
    }
  }, [isClosing]);

  return {
    close,
    containerRef,
    isClosing,
    isCollapsed,
    open,
    toggle,
  };
};
